import {
  AccordionSummary,
  Accordion,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Box,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useMemo, useState } from 'react';
import { useFilterChannelOrEvent } from '../../../../../../redux/monitoring/hooks/useFilterChannelOrEvent';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  CommanderMonitoringFilterLogicalObject,
  getNumberOfChannels,
  KEYS_CONTROL_UNITS_BY_TYPE,
} from '../../../../../../redux/monitoring/api/util/filter/filter.model';
import { PolyglotComponentProps, withPolyglot } from '../../../../../../i18n';
import { ZipDeviceControlUnit } from '../../../../../../redux/controlUnit/controlUnit.model';
import { STModal } from '../../../../../commons/Modal';
import { DeviceControlUnitsEventsMetadataAPIResponse } from '../../../../../../redux/commander/api/device.model';
import { useGroupById } from '../../../../../../redux/groups/hooks/useGroupById';

export interface DigitalChannelFiltersComposableModalButtonProps
  extends PolyglotComponentProps {
  devicesControlUnits: ZipDeviceControlUnit[];
  type: 'events' | 'channels';
  value: CommanderMonitoringFilterLogicalObject;
  onSave: (value: CommanderMonitoringFilterLogicalObject) => unknown;
  composableDeviceId: string;
}

export const DigitalChannelFiltersComposableModalButton = (
  props: DigitalChannelFiltersComposableModalButtonProps
) => {
  const { devicesControlUnits, type, polyglot, value, onSave } = props;
  const [open, setOpen] = useState(false);

  const controls = useFilterChannelOrEvent(type, devicesControlUnits, value);

  const title =
    type === 'events'
      ? polyglot.t('device.monitoring.digital_channels')
      : polyglot.t('device.monitoring.analog_channels');

  const closeModal = () => {
    setOpen(false);
  };

  const openModal = () => {
    controls.reset();
    setOpen(true);
  };

  const save = () => {
    onSave(controls.filterLogicalObject);
    closeModal();
  };

  const numberOfChannels = useMemo(
    () => getNumberOfChannels(value ?? {}),
    [value]
  );

  const selected = ` (${numberOfChannels.active}/${numberOfChannels.total})`;

  return (
    <>
      <Button
        onClick={openModal}
        startIcon={<FilterListIcon />}
        variant="outlined"
      >
        {title + selected}
      </Button>
      <STModal
        open={open}
        onClose={closeModal}
        title={title}
        fullWidth
        maxWidth="md"
        buttonActions={
          <Button onClick={save}>{polyglot.t('button.save')}</Button>
        }
      >
        <DigitalChannelFiltersComposableView
          {...props}
          controls={controls}
          devicesControlUnits={devicesControlUnits}
        />
      </STModal>
    </>
  );
};

export interface DigitalChannelFiltersComposableViewProps {
  devicesControlUnits: ZipDeviceControlUnit[];
  type: 'events' | 'channels';
  controls: ReturnType<typeof useFilterChannelOrEvent>;
  composableDeviceId: string;
}

export const DigitalChannelFiltersComposableView = (
  props: DigitalChannelFiltersComposableViewProps
) => {
  const { devicesControlUnits, type, controls, composableDeviceId } = props;
  const group = useGroupById(composableDeviceId);

  const { toggleItem, toggleDevice, getStateItem, getStateDevice } = controls;

  return (
    <Box className="DigitalChannelFiltersComposable" sx={{ width: '100%' }}>
      {devicesControlUnits.map((zipDeviceControlUnit) => (
        <Accordion
          key={zipDeviceControlUnit.deviceId}
          id={`device-title-${zipDeviceControlUnit.deviceId}`}
          aria-controls={`device-title-${zipDeviceControlUnit.deviceId}`}
          className={`DigitalChannelFiltersComposable__Device Device-${zipDeviceControlUnit.deviceId}`}
          defaultExpanded={true}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Checkbox
              id={`device-checkbox-${zipDeviceControlUnit.deviceId}`}
              inputProps={
                {
                  'data-testid': `device-checkbox-${zipDeviceControlUnit.deviceId}`,
                } as any
              }
              edge="start"
              checked={getStateDevice(zipDeviceControlUnit) === 'checked'}
              onClick={(e) => {
                e.stopPropagation();
                toggleDevice(zipDeviceControlUnit);
              }}
              indeterminate={
                getStateDevice(zipDeviceControlUnit) === 'indeterminate'
              }
              tabIndex={-1}
            />
            <Typography variant="h5" display="flex" alignItems="center">
              {group.data?.name || zipDeviceControlUnit.deviceName}
            </Typography>
          </AccordionSummary>
          <Box sx={{ padding: 1 }}>
            {zipDeviceControlUnit.controlUnits?.map((controlUnit) => (
              <List
                sx={{ pb: 0, pt: 0 }}
                key={controlUnit.id}
                className={`DigitalChannelFiltersComposable__ControlUnit Device-${zipDeviceControlUnit.deviceId} ControlUnit-${controlUnit.id}`}
                id={`control-unit-title-${zipDeviceControlUnit.deviceId}-${controlUnit.id}`}
                aria-controls={`control-unit-title-${zipDeviceControlUnit.deviceId}-${controlUnit.id}`}
              >
                {KEYS_CONTROL_UNITS_BY_TYPE[type].map((keyControlIUnit) =>
                  controlUnit[keyControlIUnit]?.map((event) => {
                    // Use composable name as primary text if available
                    const primaryText = event.name || event.id;

                    return (
                      <ListItem
                        key={event.id}
                        className={`DigitalChannelFiltersComposable__Event Event-${event.id}`}
                        disablePadding
                      >
                        <ListItemButton
                          role={undefined}
                          onClick={() =>
                            toggleItem(
                              zipDeviceControlUnit.deviceId,
                              controlUnit.id,
                              event.id
                            )
                          }
                          dense
                        >
                          <ListItemIcon>
                            <Checkbox
                              id={`event-checkbox-${zipDeviceControlUnit.deviceId}-${controlUnit.id}-${event.id}`}
                              checked={
                                getStateItem(
                                  zipDeviceControlUnit.deviceId,
                                  controlUnit.id,
                                  event.id
                                ) === 'checked'
                              }
                              indeterminate={
                                getStateItem(
                                  zipDeviceControlUnit.deviceId,
                                  controlUnit.id,
                                  event.id
                                ) === 'indeterminate'
                              }
                              tabIndex={-1}
                              disableRipple
                              inputProps={
                                {
                                  'aria-labelledby': `event-title-${zipDeviceControlUnit.deviceId}-${controlUnit.id}-${event.id}-label`,
                                  'data-testid': `event-checkbox-${zipDeviceControlUnit.deviceId}-${controlUnit.id}-${event.id}`,
                                } as any
                              }
                            />
                          </ListItemIcon>
                          <ListItemText
                            id={`event-title-${zipDeviceControlUnit.deviceId}-${controlUnit.id}-${event.id}-label`}
                            primary={primaryText}
                            primaryTypographyProps={{
                              sx: { wordBreak: 'break-all' },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })
                )}
              </List>
            ))}
          </Box>
        </Accordion>
      ))}
    </Box>
  );
};

export default withPolyglot(DigitalChannelFiltersComposableModalButton);
